(() => {
  'use strict';
  const MAX_QTY = 999;
  const localeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const getJSON = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 18000);
    try {
      const response = await fetch(url, {credentials: 'same-origin', ...options, signal: controller.signal});
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || `Request failed (${response.status})`);
      return data;
    } finally { clearTimeout(timeout); }
  };
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency}).format(cents / 100);
  const quantity = input => {
    if (!input || input.disabled) return 0;
    const raw = input.value.trim();
    if (!/^\d+$/.test(raw)) return null;
    const value = Number(raw);
    return Number.isSafeInteger(value) && value >= 0 && value <= MAX_QTY ? value : null;
  };

  async function init(section) {
    if (section.dataset.refillInitialized) return;
    section.dataset.refillInitialized = 'true';
    const root = section.querySelector('[data-ffs-refill]');
    if (!root) return;
    const add = root.querySelector('[data-refill-add]');
    // The larger prebuilt kits deliberately have no ordering controller.
    if (!add) return;
    const status = root.querySelector('[data-refill-status]');
    const total = root.querySelector('[data-refill-total]');
    const reset = root.querySelector('[data-refill-reset]');
    const cartLink = root.querySelector('[data-refill-cart]');
    cartLink.href = `${localeRoot()}cart`;
    let busy = false;
    let locked = false;
    let ready = false;
    let guide;
    let currency = window.Shopify?.currency?.active || section.dataset.currency || 'USD';
    const state = new Map();
    const announce = text => { status.textContent = text; };
    const refresh = () => {
      let count = 0; let cents = 0; let invalid = false;
      for (const item of state.values()) {
        const q = quantity(item.input);
        if (q === null) { invalid = true; item.input.setAttribute('aria-invalid', 'true'); }
        else { item.input.removeAttribute('aria-invalid'); count += q; cents += q * item.price; }
      }
      total.textContent = invalid ? `Enter whole quantities from 0 to ${MAX_QTY}.` : `${count} ${count === 1 ? 'fly' : 'flies'} selected${count ? ` · ${money(cents, currency)}` : ''}`;
      add.disabled = !ready || busy || locked || invalid || count === 0;
      return {count, cents, invalid};
    };
    const freeze = value => {
      busy = value;
      for (const item of state.values()) item.input.disabled = value || locked || !item.sellable;
      add.setAttribute('aria-busy', value ? 'true' : 'false');
      refresh();
    };
    const productReads = new Map();
    const product = handle => {
      if (!productReads.has(handle)) productReads.set(handle, getJSON(`${localeRoot()}products/${encodeURIComponent(handle)}.js`));
      return productReads.get(handle);
    };
    try {
      const data = await getJSON(section.dataset.source);
      guide = data.guides.find(g => g.handle === root.dataset.guide);
      if (!guide || guide.kind !== 'new_selection') throw new Error('This guide is not ready for individual ordering.');
      await Promise.all(guide.rows.map(async row => {
        const card = root.querySelector(`[data-row="${CSS.escape(row.key)}"]`);
        const input = card?.querySelector('[data-refill-qty]');
        const label = card?.querySelector('[data-row-status]');
        if (!input || !label) throw new Error('A refill row is incomplete.');
        const item = {row, input, price: 0, sellable: false};
        state.set(row.key, item);
        input.value = '0';
        input.addEventListener('input', refresh);
        if (!row.orderEnabled || !/^\d+$/.test(String(row.variantId))) { label.textContent = 'Exact replacement needs confirmation.'; return; }
        try {
          const current = await product(row.productHandle);
          const variant = current.variants?.find(v => String(v.id) === String(row.variantId));
          const matches = current.handle === row.productHandle && variant?.sku === row.sku && variant?.options?.includes('Single');
          const validPrice = Number.isInteger(variant?.price) && variant.price > 0;
          if (!matches || !validPrice) { label.textContent = 'Exact individual listing could not be verified. Use the product link to check it.'; return; }
          item.price = variant.price;
          item.sellable = variant.available === true;
          input.disabled = !item.sellable;
          label.textContent = `${money(item.price, currency)} each · ${item.sellable ? 'Available' : 'Currently sold out'}`;
        } catch (_) { label.textContent = 'Current availability could not be checked. Use the product link above.'; }
      }));
      ready = true;
      const active = [...state.values()].filter(x => x.sellable).length;
      announce(active ? 'Choose the individual quantities you want. Nothing is preselected.' : 'No individual rows are currently available here. Check the product links for current options.');
      refresh();
    } catch (_) {
      announce('The combined refill tool could not load. Use the exact individual-fly links above to check current options.');
      return;
    }

    async function updateDrawer(cart, sections, trigger) {
      const drawer = document.querySelector('cart-drawer');
      const wanted = drawer?.getSectionsToRender?.().map(s => s.id) || ['cart-icon-bubble'];
      if (!sections || wanted.some(id => !sections[id])) {
        try { sections = await getJSON(`${localeRoot()}cart?sections=${wanted.map(encodeURIComponent).join(',')}`); }
        catch (_) { return; }
      }
      if (drawer?.renderContents && wanted.every(id => sections[id])) {
        drawer.setActiveElement?.(trigger);
        drawer.renderContents({...cart, sections});
      } else if (sections['cart-icon-bubble']) {
        const node = document.getElementById('cart-icon-bubble');
        const fresh = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        if (node && fresh) node.innerHTML = fresh.innerHTML;
      }
    }

    reset.addEventListener('click', () => {
      if (busy) return;
      locked = false;
      for (const item of state.values()) { item.input.value = '0'; item.input.disabled = !item.sellable; }
      reset.hidden = true;
      announce('Start a new selection after checking the items already in your cart. Current availability is checked again when you submit.');
      refresh();
    });

    add.addEventListener('click', async () => {
      if (busy || locked) return;
      const selection = [...state.values()].map(item => ({...item, chosen: quantity(item.input)})).filter(item => item.chosen > 0);
      const checked = refresh();
      if (!ready || checked.invalid || !selection.length) return;
      freeze(true);
      announce('Checking your selected flies…');
      // Refresh each selected product before creating the one cart operation.
      productReads.clear();
      try {
        await Promise.all(selection.map(async item => {
          const current = await product(item.row.productHandle);
          const variant = current.variants?.find(v => String(v.id) === String(item.row.variantId));
          if (current.handle !== item.row.productHandle || variant?.sku !== item.row.sku || !variant?.options?.includes('Single') || variant.available !== true || !Number.isInteger(variant.price) || variant.price <= 0) throw new Error('A selected fly is no longer available with the same identity.');
          if (variant.price !== item.price) throw new Error('A selected price has changed. Reload this page to review current prices before adding.');
        }));
      } catch (error) {
        announce(`${error.message || 'Availability changed.'} Nothing was added. Reload the guide or open the individual listing.`);
        freeze(false);
        return;
      }
      const operation = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer?.getSectionsToRender?.().map(s => s.id) || ['cart-icon-bubble'];
      const payload = {
        items: selection.map(item => ({id: Number(item.row.variantId), quantity: item.chosen, properties: {'Refill for': guide.title, '_refill_version': guide.key, '_ffs_refill_operation': operation}})),
        sections: sectionIds,
        sections_url: location.pathname
      };
      let responseData = null;
      let errorMessage = '';
      let completedResponse = false;
      announce('Adding your selected individual flies…');
      try {
        // Never retry this mutation automatically: a lost response may still have added items.
        responseData = await getJSON(`${localeRoot()}cart/add.js`, {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify(payload)});
        completedResponse = true;
      } catch (error) { errorMessage = error.message || 'The store did not confirm the cart request.'; }
      let cart = null;
      try { cart = await getJSON(`${localeRoot()}cart.js`); } catch (_) { /* Keep the result uncertain; do not repeat the add. */ }
      if (cart?.items && Array.isArray(cart.items)) {
        const own = cart.items.filter(line => line.properties?._ffs_refill_operation === operation);
        const actual = new Map();
        for (const line of own) actual.set(String(line.variant_id || line.id), (actual.get(String(line.variant_id || line.id)) || 0) + line.quantity);
        const expected = new Map(selection.map(item => [String(item.row.variantId), item.chosen]));
        const allConfirmed = actual.size === expected.size && [...expected].every(([id, count]) => actual.get(id) === count);
        const addedCount = own.reduce((sum, line) => sum + line.quantity, 0);
        if (allConfirmed && completedResponse) {
          for (const item of state.values()) item.input.value = '0';
          announce(`Added ${addedCount} ${addedCount === 1 ? 'individual fly' : 'individual flies'} to your cart. Review your cart before checkout.`);
          freeze(false);
          await updateDrawer(cart, responseData?.sections, add);
          return;
        }
        locked = true;
        announce(addedCount > 0
          ? `${addedCount} ${addedCount === 1 ? 'fly is' : 'flies are'} confirmed in your cart from this selection. ${errorMessage || 'The complete selection could not be confirmed.'} Review your cart before adding more; this request will not be retried.`
          : `${errorMessage || 'The cart result could not be confirmed.'} Review your cart before trying a new selection. We have not retried the request.`);
      } else {
        locked = true;
        announce('The cart result could not be checked. Your request may have added items. Review your cart before starting a new selection; we will not repeat it automatically.');
      }
      reset.hidden = false;
      freeze(false);
    });
  }
  const start = container => container.querySelectorAll('[data-ffs-refill-section]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(document));
  else start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
